import React from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import Snackbar from '~/components/Snackbar';
import useTeams from '~/data/hooks/useTeams';
import { CustomPagesSetupConfig, ModuleInstance } from '~/data/models/yearbook';
import {
  MODULE_INSTANCE,
  UPDATE_CUSTOM_PAGES_SETUP,
  UPDATE_MODULE_INSTANCE,
} from '~/data/operations/yearbook';
import { YearbookStackParamList } from '~/navigation/types';
import { UpdateModuleInstanceInput } from '../layout/ModuleSetupLayout';
import CustomSetupLayout, { UpdateCustomPagesSetupInput } from './layout';

type CustomSetupNavProp = StackNavigationProp<
  YearbookStackParamList,
  'CustomSetup'
>;

type CustomSetupRouteProp = RouteProp<YearbookStackParamList, 'CustomSetup'>;

export default function CustomSetup(): JSX.Element {
  const navigation = useNavigation<CustomSetupNavProp>();
  const {
    params: { moduleInstanceId },
  } = useRoute<CustomSetupRouteProp>();
  const {
    teams,
    loading: teamsLoading,
    onLoadMore: onLoadMoreTeams,
  } = useTeams();
  const [updateCustomPagesSetup] = useMutation(UPDATE_CUSTOM_PAGES_SETUP);
  const [updateModuleInstance] = useMutation(UPDATE_MODULE_INSTANCE);

  const { data: moduleInstanceData, loading: moduleInstanceLoading } = useQuery(
    MODULE_INSTANCE,
    {
      variables: {
        id: moduleInstanceId,
      },
    },
  );

  const moduleInstance = moduleInstanceData?.moduleInstance;
  const customSetupConfig =
    moduleInstanceData?.moduleInstance?.customPagesSetup;

  const onUpdateModuleInstance = async ({
    isActive,
    dueDate,
  }: UpdateModuleInstanceInput) => {
    try {
      await updateModuleInstance({
        variables: {
          input: {
            id: moduleInstanceId,
            isActive,
            dueDate,
          },
        },
      });
    } catch (e) {
      if (e instanceof Error) {
        Snackbar.show(e.message);
      }
    }
  };

  const onUpdateCustomPagesSetup = async ({
    addCustomPagesTeamId,
    viewCustomPagesTeamId,
    quoteContent,
  }: UpdateCustomPagesSetupInput) => {
    try {
      if (moduleInstance && customSetupConfig) {
        await updateCustomPagesSetup({
          variables: {
            input: {
              id: customSetupConfig.id,
              isActive: moduleInstance.isActive,
              dueDate: moduleInstance.dueDate,
              createPagesTeam: addCustomPagesTeamId,
              viewPagesTeam: viewCustomPagesTeamId,
              guide: quoteContent,
            },
          },
        });
      }
    } catch (e) {
      if (e instanceof Error) {
        Snackbar.show(e.message);
      }
    }
  };
  const loading = !moduleInstance || moduleInstanceLoading || teamsLoading;

  return (
    <CustomSetupLayout
      onBack={() => navigation.goBack()}
      moduleInstance={moduleInstance as ModuleInstance}
      onUpdateModuleInstance={onUpdateModuleInstance}
      teams={teams}
      loading={loading}
      customSetupConfig={customSetupConfig as CustomPagesSetupConfig}
      onLoadMoreTeams={onLoadMoreTeams}
      onUpdateCustomPagesSetup={onUpdateCustomPagesSetup}
    />
  );
}
